/**
 * NotificationsDropdown Component
 * Displays a dropdown panel showing recent notifications with preview
 */

import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellOutlined, CalendarOutlined, FileTextOutlined, DollarCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNotificationContext } from './NotificationContext';
import { handleNotificationClick } from '../../utils/notificationHandler';
import './notifications-dropdown.css';

/**
 * Format notification time to relative time string
 */
const formatNotificationTime = (timestamp) => {
  if (!timestamp) return 'Just now';
  
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 1) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

/**
 * Get icon for notification category
 */
const getNotificationIcon = (category) => {
  const iconMap = {
    appointments: <CalendarOutlined />,
    pa: <FileTextOutlined />,
    claims: <DollarCircleOutlined />,
    encounters: <CheckCircleOutlined />,
    system: <BellOutlined />,
  };
  return iconMap[category] || <BellOutlined />;
};

/**
 * NotificationsDropdown Component
 * Displays recent notifications in a dropdown panel
 */
const NotificationsDropdown = ({ onClose }) => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const { unreadCount, notifications, markAsRead, markAllAsRead } = useNotificationContext();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Don't close if clicking on the sidebar item that opened it
        const sidebarItem = event.target.closest('.sidebar-item');
        if (!sidebarItem || !sidebarItem.classList.contains('has-dropdown')) {
          onClose?.();
        }
      }
    };

    // Add event listener after a short delay to avoid immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleNotificationItemClick = (notification) => {
    // Mark as read if not already read (support both isRead and read fields)
    const isUnread = !(notification.isRead || notification.read);
    if (isUnread && markAsRead) {
      markAsRead(notification.id);
    }
    
    // Use the centralized handler for deep linking
    // This properly handles route building with parameters
    handleNotificationClick(notification, navigate);
    
    // Close dropdown
    onClose?.();
  };

  const handleMarkAllAsRead = (e) => {
    e.stopPropagation();
    markAllAsRead();
    onClose?.();
  };

  const handleViewAll = (e) => {
    e.stopPropagation();
    navigate('/notifications');
    onClose?.();
  };

  const handleViewCenter = (e) => {
    e.stopPropagation();
    navigate('/notifications');
    onClose?.();
  };

  return (
    <div ref={dropdownRef} className="notifications-dropdown" onClick={(e) => e.stopPropagation()}>
      {/* Header */}
      <div className="notifications-dropdown-header">
        <span className="notifications-dropdown-title">
          Recent Notifications
        </span>
        <span className="notifications-dropdown-count">
          {unreadCount} unread
        </span>
      </div>

      {/* Notifications List */}
      {notifications && notifications.length > 0 ? (
        <>
          <div className="notifications-dropdown-list">
            {notifications.slice(0, 5).map(notification => {
              // Support both isRead and read fields for backward compatibility
              const isUnread = !(notification.isRead || notification.read);
              
              const category = notification.category || 'system';
              return (
              <div
                key={notification.id}
                className={`notifications-dropdown-item ${isUnread ? 'unread' : 'read'} category-${category}`}
                onClick={() => handleNotificationItemClick(notification)}
              >
                <div className="notifications-dropdown-item-icon">
                  {getNotificationIcon(notification.category)}
                </div>
                <div className="notifications-dropdown-item-content">
                  <div className="notifications-dropdown-item-title">
                    {isUnread && <span className="notifications-dropdown-unread-dot" aria-hidden />}
                    {notification.title}
                  </div>
                  <div className="notifications-dropdown-item-body">
                    {notification.body}
                  </div>
                  <div className="notifications-dropdown-item-time">
                    {formatNotificationTime(notification.createdAt)}
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          {/* Show "View All" if more than 5 notifications */}
          {notifications.length > 5 && (
            <div className="notifications-dropdown-more">
              <button
                onClick={handleViewAll}
                className="notifications-dropdown-more-btn"
              >
                View All Notifications →
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="notifications-dropdown-empty">
          <BellOutlined className="empty-icon" />
          <p>No notifications yet</p>
        </div>
      )}

      {/* Footer Actions */}
      <div className="notifications-dropdown-footer">
        <button
          onClick={handleMarkAllAsRead}
          disabled={unreadCount === 0}
          className="notifications-dropdown-mark-all-btn"
        >
          Mark All as Read
        </button>
        <button
          onClick={handleViewCenter}
          className="notifications-dropdown-view-center-btn"
        >
          View Notification Center
        </button>
      </div>
    </div>
  );
};

export default NotificationsDropdown;

